import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminRole } from '@/types';

const ADMIN_ROLE_CACHE_KEY = 'admin_role_cache';
const CACHE_EXPIRY_HOURS = 24;

interface CachedRole {
  role: AdminRole;
  timestamp: number;
}

export function useAdminRole() {
  const [adminRole, setAdminRole] = useState<AdminRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    checkAdminRole();
  }, []);

  const getCachedRole = (): CachedRole | null => {
    const cached = localStorage.getItem(ADMIN_ROLE_CACHE_KEY);
    if (!cached) return null;

    const { role, timestamp } = JSON.parse(cached);
    const expiryTime = timestamp + (CACHE_EXPIRY_HOURS * 60 * 60 * 1000);

    if (Date.now() > expiryTime) {
      localStorage.removeItem(ADMIN_ROLE_CACHE_KEY);
      return null;
    }

    return { role, timestamp };
  };

  const setCachedRole = (role: AdminRole) => {
    const cacheData: CachedRole = {
      role,
      timestamp: Date.now()
    };
    localStorage.setItem(ADMIN_ROLE_CACHE_KEY, JSON.stringify(cacheData));
  };

  const checkAdminRole = async () => {
    try {
      // Check cache first
      const cachedRole = getCachedRole();
      if (cachedRole) {
        setAdminRole(cachedRole.role);
        setLoading(false);
        return;
      }

      // If no cache, check Supabase
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setAdminRole(null);
        setLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('admin_role')
        .eq('id', user.id)
        .single();

      if (profile?.admin_role) {
        setCachedRole(profile.admin_role);
        setAdminRole(profile.admin_role);
      } else {
        setAdminRole(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to check admin role'));
      setAdminRole(null);
    } finally {
      setLoading(false);
    }
  };

  const clearCache = () => {
    localStorage.removeItem(ADMIN_ROLE_CACHE_KEY);
    setAdminRole(null);
  };

  return {
    adminRole,
    loading,
    error,
    clearCache,
    refreshRole: checkAdminRole
  };
}